import React, { useState } from "react";
import InstaColors, { useSharedValue } from "../components/Constants/constants";
import { headerStyle, sectionStyle } from "./formConstants";
import { FormCheckBox, FormDate, FormInput, FormTitle, createRequiredLabel } from "./common";
import "../Forms/style.css";
import CountryDropdown from "./CountryDropdown";
import { W8ECIFormModel } from "../Models/W8ECIModels";
import { useW8ECIForm } from "../Hooks/useW8ECIForm";
import { W8ECIService } from "../services/W8ECIService";
import { validateTINFormat } from "../utils/formUtils";

// Add utility function for address parsing
const parseAddressComponents = (addressString: string) => {
  if (!addressString) return { city: "", state: "", postalCode: "" };

  // Split by comma and trim whitespace
  const parts = addressString.split(",").map((part) => part.trim());

  // Initialize result object
  const result = {
    city: "",
    state: "",
    postalCode: "",
  };

  // If we have at least one part, it's the city
  if (parts.length > 0) {
    result.city = parts[0];
  }

  // If we have a second part, it's the state
  if (parts.length > 1) {
    result.state = parts[1];
  }

  // If we have a third part, it's the postal code
  if (parts.length > 2) {
    result.postalCode = parts[2];
  }

  return result;
};

export class W8ECIFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const W8ECIFORM = ({ selectedRowData, row, selectedIndex = 0, onSaveSuccess }: W8ECIFormProps) => {
  const { isMenuOpen } = useSharedValue();

  // Use W8ECI form hook for state management
  const {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  } = useW8ECIForm({
    initialData: selectedRowData,
  });

  // Legacy form data state for UI compatibility
  const [formData, setFormData] = useState(() => {
    // Convert model to form data format for legacy UI
    const modelData = model as any;
    return {
      ...modelData,
    };
  });

  // Update formData whenever model changes
  React.useEffect(() => {
    setFormData({ ...model });
  }, [model]);

  // Watch for changes in selectedRowData and update form fields accordingly
  React.useEffect(() => {
    if (selectedRowData) {
      // Check if mapping is already done to prevent overwriting user edits
      if (formData.isMapDone) {
        console.log(
          "W8ECI form mapping already done, skipping automatic field updates to preserve user edits"
        );

        // Special case: Even if mapping is done, check if we need to populate income items from ECI-11
        if (
          selectedRowData["ECI-11_Effectively_connected_income_details"] &&
          incomeItems.length === 1 &&
          incomeItems[0].incomeText === "" &&
          incomeItems[0].incomeType === "" &&
          incomeItems[0].incomeAmount === ""
        ) {
          const eciIncomeDetails = selectedRowData["ECI-11_Effectively_connected_income_details"];
          const newIncomeItem = {
            incomeType: "",
            incomeText: eciIncomeDetails,
            incomeAmount: "",
          };
          setIncomeItems([newIncomeItem]);
          updateField("ECI_income_text_0", eciIncomeDetails);
        }

        return;
      }

      // Check if UniqueIdentifier has changed and update it
      const newUniqueIdentifier =
        selectedRowData.uniqueidentifier || selectedRowData.UniqueIdentifier;
      const currentUniqueIdentifier = formData.UniqueIdentifier;

      if (newUniqueIdentifier && newUniqueIdentifier !== currentUniqueIdentifier) {
        updateField("UniqueIdentifier", newUniqueIdentifier);
      }

      // Auto-set signature fields if signature exists
      if (selectedRowData["Signature"] && selectedRowData["Signature"].toString().trim() !== "") {
        updateField("isSigned", "yes");
      }

      // Auto-set dealer in securities checkbox based on API response
      if (
        selectedRowData["ECI-12_Securities_dealer"] === "selected" ||
        selectedRowData["ECI-12_Securities_dealer"] === true ||
        selectedRowData["ECI-12_Securities_dealer"] === "true"
      ) {
        updateField("ECI-12_Securities_dealer", "selected");
      }

      // Debug log to see what data is coming from API
      console.log("🔍 W8ECI API Debug - selectedRowData keys:", Object.keys(selectedRowData));
      console.log(
        "🔍 W8ECI API Debug - ECI-11_Effectively_connected_income_details value:",
        selectedRowData["ECI-11_Effectively_connected_income_details"]
      );

      // Populate income items from API data
      const extractedIncomeItems: any[] = [];
      let incomeIndex = 0;

      // Look for income fields with pattern ECI_income_type_0, ECI_income_text_0, etc.
      while (
        selectedRowData[`ECI_income_type_${incomeIndex}`] ||
        selectedRowData[`ECI_income_text_${incomeIndex}`] ||
        selectedRowData[`ECI_income_amount_${incomeIndex}`]
      ) {
        extractedIncomeItems.push({
          incomeType: selectedRowData[`ECI_income_type_${incomeIndex}`] || "",
          incomeText: selectedRowData[`ECI_income_text_${incomeIndex}`] || "",
          incomeAmount: selectedRowData[`ECI_income_amount_${incomeIndex}`] || "",
        });
        incomeIndex++;
      }

      if (extractedIncomeItems.length > 0) {
        setIncomeItems(extractedIncomeItems);
      } else if (selectedRowData["ECI-11_Effectively_connected_income_details"]) {
        // Map ECI-11_Effectively_connected_income_details to first income item's incomeText
        const eciIncomeDetails = selectedRowData["ECI-11_Effectively_connected_income_details"];

        const newIncomeItem = {
          incomeType: "",
          incomeText: eciIncomeDetails,
          incomeAmount: "",
        };
        setIncomeItems([newIncomeItem]);
      }

      if (selectedRowData["ECI-EIN"] === true || selectedRowData["ECI-EIN"] === "selected") {
        updateField("Taxpayer_type", "EIN");
        updateField("ECI-EIN", true);
      } else if (
        selectedRowData["ECI-Us_tin_SSN/ITIN"] === true ||
        selectedRowData["ECI-Us_tin_SSN/ITIN"] === "selected"
      ) {
        updateField("Taxpayer_type", "SSN/ITIN");
        updateField("ECI-Us_tin_SSN/ITIN", true);
      }

      // Update other fields that might have changed
      Object.keys(selectedRowData).forEach((key) => {
        const value = selectedRowData[key];
        const currentValue = (formData as any)[key];

        // Only update if the value has actually changed and is not empty
        if (value && value !== currentValue && typeof value === "string") {
          updateField(key, value);
        }
      });

      // Set mapping done flag to prevent re-mapping and preserve user edits
      updateField("isMapDone", true);
    }
  }, [selectedRowData, updateField, formData.isMapDone]);

  // Auto-detect and set Taxpayer type based on TIN value from API
  React.useEffect(() => {
    const tinValue = formData["U.S. taxpayer identification number"];
    const currentTaxpayerType = formData["Taxpayer_type"];

    if (tinValue && tinValue.trim() !== "") {
      const trimmedTin = tinValue.trim();

      // Validate TIN format and get detected type
      const validation = validateTINFormat(trimmedTin, currentTaxpayerType);

      // Set validation message (warnings or success)
      setTinValidationMessage(validation.message);

      // If type was detected and it's different from current, update it
      if (validation.detectedType && validation.detectedType !== currentTaxpayerType) {
        // W-8ECI has separate EIN and SSN/ITIN options
        let formType = validation.detectedType;
        if (validation.detectedType === "SSN" || validation.detectedType === "ITIN") {
          formType = "SSN/ITIN";
        }

        updateField("Taxpayer_type", formType);

        // Also update the checkboxes
        if (formType === "EIN") {
          updateField("ECI-EIN", true);
          updateField("ECI-Us_tin_SSN/ITIN", false);
        } else if (formType === "SSN/ITIN") {
          updateField("ECI-Us_tin_SSN/ITIN", true);
          updateField("ECI-EIN", false);
        }
      }
      // If no type was detected, show warning but don't update type
      else if (!validation.detectedType && validation.message) {
        // console.log('⚠️ TIN from API could not be auto-detected:', {
        //   tin: trimmedTin,
        //   message: validation.message
        // });
      }
    } else {
      // Clear validation message when TIN is empty
      setTinValidationMessage(null);
    }
  }, [formData["U.S. taxpayer identification number"], formData["Taxpayer_type"], updateField]);

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // State for TIN validation message
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  // Income items state for ECI specific functionality
  const emptyIncomeItem = {
    incomeType: "",
    incomeText: "",
    incomeAmount: "",
  };

  const [incomeItems, setIncomeItems] = useState<any[]>(() => {
    // Initialize income items from model if available
    if (selectedRowData && selectedRowData.incomeItems) {
      try {
        return typeof selectedRowData.incomeItems === "string"
          ? JSON.parse(selectedRowData.incomeItems)
          : selectedRowData.incomeItems;
      } catch (error) {
        console.error("Error parsing income items:", error);
        return [{ ...emptyIncomeItem }];
      }
    }
    return [{ ...emptyIncomeItem }];
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;

    // Handle Taxpayer_type dropdown change
    if (name === "Taxpayer_type") {
      // When type changes, clear the TIN input so user can enter new value
      updateField(name, value);
      updateField("U.S. taxpayer identification number", "");

      // Clear validation message
      setTinValidationMessage("");

      // Update checkboxes to match type
      if (value === "EIN") {
        updateField("ECI-EIN", true);
        updateField("ECI-Us_tin_SSN/ITIN", false);
      } else if (value === "SSN/ITIN") {
        updateField("ECI-Us_tin_SSN/ITIN", true);
        updateField("ECI-EIN", false);
      }

      return;
    }

    // Handle TIN validation and auto-detection
    if (name === "U.S. taxpayer identification number") {
      const tinValue = value.toString().trim();
      const currentType = formData["Taxpayer_type"];

      if (tinValue) {
        // Validate TIN format
        const validation = validateTINFormat(tinValue, currentType);

        // Set validation message
        setTinValidationMessage(validation.message);

        // If valid and detected type exists, and NO type is selected, auto-select it
        if (
          validation.isValid &&
          validation.detectedType &&
          (!currentType || currentType === "" || currentType === "---Select---")
        ) {
          let formType = validation.detectedType;
          if (formType === "SSN" || formType === "ITIN") {
            formType = "SSN/ITIN";
          }

          // Update type and checkboxes
          updateField("Taxpayer_type", formType);
          if (formType === "EIN") {
            updateField("ECI-EIN", true);
            updateField("ECI-Us_tin_SSN/ITIN", false);
          } else if (formType === "SSN/ITIN") {
            updateField("ECI-Us_tin_SSN/ITIN", true);
            updateField("ECI-EIN", false);
          }
        }
      } else {
        setTinValidationMessage(null);
      }
    }

    // Handle securities dealer checkbox
    if (name === "ECI-12_Securities_dealer") {
      updateField(name, checked ? "selected" : "");
      return;
    }

    // Update the model through the hook
    updateField(name, type === "checkbox" ? checked : value);
  };

  const handleIncomeItemChange = (
    idx: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setIncomeItems((prev) =>
      prev.map((item, i) => {
        if (i === idx) {
          return { ...item, [name]: value };
        }
        return item;
      })
    );

    // Also update the model with the ECI-income-* prefixed fields
    const updates: any = {};
    updates[`ECI_income_type_${idx}`] =
      name === "incomeType" ? value : incomeItems[idx]?.incomeType || "";
    updates[`ECI_income_text_${idx}`] =
      name === "incomeText" ? value : incomeItems[idx]?.incomeText || "";
    updates[`ECI_income_amount_${idx}`] =
      name === "incomeAmount" ? value : incomeItems[idx]?.incomeAmount || "";

    // // Map incomeText to ECI-11_Effectively_connected_income_details field
    // if (name === 'incomeText') {
    //   updates['ECI-11_Effectively_connected_income_details'] = value;
    // }

    updateMultipleFields(updates);
  };

  const handleAddIncomeItem = () => {
    setIncomeItems([...incomeItems, { ...emptyIncomeItem }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      console.error("Form validation failed");
      // Show validation summary
      setShowValidationErrors(true);
      // Show validation error alert
      const errorMessages = Object.values(errors).join("\n");
      alert(`Please fix the following errors before submitting:\n\n${errorMessages}`);
      return;
    }

    try {
      // Use the model directly as the source of truth for current form values
      const currentFormValues: any = {};

      // Get all current form values from the model (which is always up-to-date)
      Object.keys(model).forEach((key) => {
        currentFormValues[key] = (model as any)[key];
      });

      // Add income items data
      const incomeItemsString = JSON.stringify(incomeItems);
      currentFormValues.incomeItems = incomeItemsString;

      // Add individual income fields
      incomeItems.forEach((item, idx) => {
        currentFormValues[`ECI_income_type_${idx}`] = item.incomeType || "";
        currentFormValues[`ECI_income_text_${idx}`] = item.incomeText || "";
        currentFormValues[`ECI_income_amount_${idx}`] = item.incomeAmount || "";
      });

      // Set mapping done flag to prevent re-mapping
      currentFormValues.isMapDone = true;

      // Remove specific fields before saving
      currentFormValues["Permanent city address"] = "";
      currentFormValues["ECI-Business_address_city_state_zip"] = "";
      delete currentFormValues["ECI-11_Effectively_connected_income_details"];

      // Create a new model with the current form values
      const saveModel = new W8ECIFormModel(currentFormValues);

      // Save directly using the service with our collected current values
      const success = await W8ECIService.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save W-8ECI form:", error);
      alert("Failed to save form data");
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>{field.replace("ECI-", "").replace(/_/g, " ")}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Part I: Identification of Beneficial Owner */}
            <section style={sectionStyle}>
              <FormInput
                label={createRequiredLabel("Unique Form Identifier")}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={(formData["UniqueIdentifier"] as string) || ""}
                onChange={handleChange}
              />
              <FormTitle value={"Part I: Identification of Beneficial Owner"} />
              <FormInput
                label={"First name and middle name:"}
                id={"First name and middle name"}
                name={"First name and middle name"}
                value={(formData["First name and middle name"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Last name:"}
                id={"Last name"}
                name={"Last name"}
                value={(formData["Last name"] as string) || ""}
                onChange={handleChange}
              />
              {/* <FormInput label={"Name:"} id={"Name"} name={"Name"} value={formData["Name"] as string || ''} onChange={handleChange} /> */}
              <FormInput
                label={"Name of disregarded entity:"}
                id={"ECI-Disregarded_entity_name"}
                name={"ECI-Disregarded_entity_name"}
                value={(formData["ECI-Disregarded_entity_name"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", flexDirection: "column", marginBottom: 16 }}>
                <FormDate
                  label={"Date of birth:"}
                  name={"Date of birth"}
                  value={(formData["Date of birth"] as string) || ""}
                  onChange={handleChange}
                />
                <span style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  (Enter in U.S. date format: MM/DD/YYYY)
                </span>
              </div>
              <CountryDropdown
                label="Country of organization:"
                name="Country_of_incorporation_organization"
                id="Country_of_incorporation_organization"
                value={
                  typeof formData["Country_of_incorporation_organization"] === "string"
                    ? formData["Country_of_incorporation_organization"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Country_of_incorporation_organization",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Other"}
                name={"Other"}
                value={(formData["Other"] as string) || ""}
                onChange={handleChange}
              />

              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <label style={{ fontWeight: 500, display: "block", marginBottom: 8 }}>
                  User Record types:
                </label>
                <FormCheckBox
                  label={"Central Bank of Issue"}
                  name={"ECI-Central_bank_of_issue"}
                  checked={!!formData["ECI-Central_bank_of_issue"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Complex Trust"}
                  name={"ECI-Complex_trust"}
                  checked={!!formData["ECI-Complex_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Corporation"}
                  name={"ECI-Corporation"}
                  checked={!!formData["ECI-Corporation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Estate"}
                  name={"ECI-Estate"}
                  checked={!!formData["ECI-Estate"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Government - Controlled Entity"}
                  name={"ECI-Government_controlled_entity"}
                  checked={!!formData["ECI-Government_controlled_entity"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Government - Integral Part"}
                  name={"ECI-Government_integral"}
                  checked={!!formData["ECI-Government_integral"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Grantor Trust"}
                  name={"ECI-Grantor_trust"}
                  checked={!!formData["ECI-Grantor_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Individual"}
                  name={"ECI-Individual"}
                  checked={!!formData["ECI-Individual"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"International Organization"}
                  name={"ECI-International_organization"}
                  checked={!!formData["ECI-International_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Partnership"}
                  name={"ECI-Partnership"}
                  checked={!!formData["ECI-Partnership"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Private Foundation"}
                  name={"ECI-Private_foundation"}
                  checked={!!formData["ECI-Private_foundation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Simple Trust"}
                  name={"ECI-Simple_trust"}
                  checked={!!formData["ECI-Simple_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Tax-Exempt Organization"}
                  name={"ECI-Tax_exempt_organization"}
                  checked={!!formData["ECI-Tax_exempt_organization"]}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Permanent address section (copied from W-8BEN) */}
            <section style={sectionStyle}>
              <FormTitle value={"Permanent address"} />
              <FormInput
                label={"Line 1:"}
                id={"Permanent residence address"}
                name={"Permanent residence address"}
                value={(formData["Permanent residence address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Permanent_address_line_2"}
                name={"Permanent_address_line_2"}
                value={(formData["Permanent_address_line_2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent_address_line_3"}
                name={"Permanent_address_line_3"}
                value={(formData["Permanent_address_line_3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Permanent_address_city"}
                name={"Permanent_address_city"}
                value={(formData["Permanent_address_city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent_address_state_USCA"}
                    name={"Permanent_address_state_USCA"}
                    value={(formData["Permanent_address_state_USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>Non U.S.:</span> State / Province as it
                    appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent_address_state_NonUS"}
                    name={"Permanent_address_state_NonUS"}
                    value={(formData["Permanent_address_state_NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent_address_zip"}
                name={"Permanent_address_zip"}
                value={(formData["Permanent_address_zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Permanent_address_country"
                id="Permanent_address_country"
                value={
                  typeof formData["Permanent address country"] === "string"
                    ? formData["Permanent address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Permanent address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Permanent_address_other"}
                name={"Permanent_address_other"}
                value={(formData["Permanent_address_other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent_address_is_care_of"
                      value="yes"
                      checked={formData["Permanent_address_is_care_of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent_address_is_care_of"
                      value="no"
                      checked={formData["Permanent_address_is_care_of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent_address_is_po_box"
                      value="yes"
                      checked={formData["Permanent_address_is_po_box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent_address_is_po_box"
                      value="no"
                      checked={formData["Permanent_address_is_po_box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent_address_is_rural_route"
                      value="yes"
                      checked={formData["Permanent_address_is_rural_route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent_address_is_rural_route"
                      value="no"
                      checked={formData["Permanent_address_is_rural_route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </section>

            {/* Business address section (same as permanent address) */}
            <section style={sectionStyle}>
              <FormTitle value={"Business address (Normally in the U.S.)"} />
              <FormInput
                label={"Line 1:"}
                id={"ECI-Business_address_street"}
                name={"ECI-Business_address_street"}
                value={(formData["ECI-Business_address_street"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"ECI-Business_address_line_2"}
                name={"ECI-Business_address_line_2"}
                value={(formData["ECI-Business_address_line_2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"ECI-Business_address_line_3"}
                name={"ECI-Business_address_line_3"}
                value={(formData["ECI-Business_address_line_3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Business_address_city"}
                name={"Business_address_city"}
                value={(formData["Business_address_city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Business_address_state_USCA"}
                    name={"Business_address_state_USCA"}
                    value={(formData["Business_address_state_USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>Non U.S.:</span> State / Province as it
                    appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Business_address_state_NonUS"}
                    name={"Business_address_state_NonUS"}
                    value={(formData["Business_address_state_NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Business_address_zip"}
                name={"Business_address_zip"}
                value={(formData["Business_address_zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Business_address_country"
                id="Business_address_country"
                value={
                  typeof formData["Business_address_country"] === "string"
                    ? formData["Business_address_country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Business_address_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Business_address_other"}
                name={"Business_address_other"}
                value={(formData["Business_address_other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is care-of-address:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Business_address_is_care_of"
                      value="yes"
                      checked={formData["Business_address_is_care_of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Business_address_is_care_of"
                      value="no"
                      checked={formData["Business_address_is_care_of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is P.O. Box:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Business_address_is_po_box"
                      value="yes"
                      checked={formData["Business_address_is_po_box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Business_address_is_po_box"
                      value="no"
                      checked={formData["Business_address_is_po_box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is rural route:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Business_address_is_rural_route"
                      value="yes"
                      checked={formData["Business_address_is_rural_route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Business_address_is_rural_route"
                      value="no"
                      checked={formData["Business_address_is_rural_route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </section>

            {/* Taxpayer identification section (copied from W-8BEN) */}
            <section style={sectionStyle}>
              <FormTitle value={"Taxpayer identification"} />
              <FormInput
                label="Email:"
                id="Taxpayer_email"
                name="Taxpayer_email"
                value={(formData["Taxpayer_email"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="Taxpayer_type"
                  style={{ fontWeight: 500, display: "block", marginBottom: 4 }}
                >
                  Type:
                </label>
                <select
                  id="Taxpayer_type"
                  name="Taxpayer_type"
                  value={
                    typeof formData["Taxpayer_type"] === "string" ? formData["Taxpayer_type"] : ""
                  }
                  onChange={(e) => {
                    // Update the dropdown value
                    handleChange({
                      target: {
                        name: "Taxpayer_type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);

                    // Update the corresponding checkboxes for XML generation
                    if (e.target.value === "EIN") {
                      updateField("ECI-EIN", true);
                      updateField("ECI-Us_tin_SSN/ITIN", false);
                    } else if (e.target.value === "SSN/ITIN") {
                      updateField("ECI-Us_tin_SSN/ITIN", true);
                      updateField("ECI-EIN", false);
                    } else {
                      updateField("ECI-EIN", false);
                      updateField("ECI-Us_tin_SSN/ITIN", false);
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="EIN">EIN</option>
                  <option value="SSN/ITIN">SSN/ITIN</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormInput
                  label="TIN:"
                  id="U.S. taxpayer identification number"
                  name="U.S. taxpayer identification number"
                  value={(formData["U.S. taxpayer identification number"] as string) || ""}
                  onChange={handleChange}
                />
                {tinValidationMessage && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: "6px 8px",
                      backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                      border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                      borderRadius: 4,
                      fontSize: "12px",
                      color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                    }}
                  >
                    {tinValidationMessage}
                  </div>
                )}
              </div>
              <FormInput
                label="Foreign taxpayer id:"
                id="Foreign tax identifying number"
                name="Foreign tax identifying number"
                value={(formData["Foreign tax identifying number"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <input
                  type="checkbox"
                  id="FTIN_not_legally_required"
                  name="FTIN_not_legally_required"
                  checked={!!formData["FTIN_not_legally_required"]}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "FTIN_not_legally_required",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{ marginRight: 8 }}
                />
                <label htmlFor="FTIN_not_legally_required" style={{ fontWeight: 500 }}>
                  Check if FTIN not legally required :
                </label>
              </div>
              <FormInput
                label="Reference numbers:"
                id="ECI-Reference_number"
                name="ECI-Reference_number"
                value={(formData["ECI-Reference_number"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Items of income section */}
            <section style={sectionStyle}>
              <FormTitle value={"Items of income"} />
              {incomeItems.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: 32,
                    borderBottom: idx < incomeItems.length - 1 ? "1px solid #eee" : "none",
                    paddingBottom: 24,
                  }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <label
                      htmlFor={`incomeType-${idx}`}
                      style={{ fontWeight: 500, display: "block", marginBottom: 4 }}
                    >
                      Item of income:
                    </label>
                    <select
                      id={`incomeType-${idx}`}
                      name="incomeType"
                      value={item.incomeType}
                      onChange={(e) => handleIncomeItemChange(idx, e)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                      }}
                    >
                      <option value="">---Select---</option>
                      <option value="01-Interest (Portfolio Interest)">
                        01-Interest (Portfolio Interest)
                      </option>
                      <option value="01-Interest paid by U.S. obligors - general">
                        01-Interest paid by U.S. obligors - general
                      </option>
                      <option value="02-Interest paid on real property mortgages">
                        02-Interest paid on real property mortgages
                      </option>
                      <option value="03-Interest paid to controlling foreign corporations">
                        03-Interest paid to controlling foreign corporations
                      </option>
                      <option value="04-Interest paid by foreign corporations">
                        04-Interest paid by foreign corporations
                      </option>
                      <option value="05-Interest on tax-free covenant bonds">
                        05-Interest on tax-free covenant bonds
                      </option>
                      <option value="06-Dividends paid by U.S. corporations - general">
                        06-Dividends paid by U.S. corporations - general
                      </option>
                      <option value="06-Dividends paid by U.S. corporations - TIN required">
                        06-Dividends paid by U.S. corporations - TIN required
                      </option>
                      <option value="07-Dividends qualifying for direct dividend rate">
                        07-Dividends qualifying for direct dividend rate
                      </option>
                      <option value="08-Dividends paid by foreign corporations">
                        08-Dividends paid by foreign corporations
                      </option>
                      <option value="10-Industrial Royalties - Know-how/Other Industrial Royalties">
                        10-Industrial Royalties - Know-how/Other Industrial Royalties
                      </option>
                      <option value="09-Capital gains">09-Capital gains</option>
                      <option value="10-Industrial Royalties - Patents">
                        10-Industrial Royalties - Patents
                      </option>
                      <option value="10-Industrial Royalties - Industrial Equipment">
                        10-Industrial Royalties - Industrial Equipment
                      </option>
                      <option value="11-Motion picture or television copyright royalties">
                        11-Motion picture or television copyright royalties
                      </option>
                      <option value="12-Other royalties (for example, copyright, software, broadcasting, endorsement payments)">
                        12-Other royalties (for example, copyright, software, broadcasting,
                        endorsement payments)
                      </option>
                      <option value="13-Royalties paid on certain publicly offered securities">
                        13-Royalties paid on certain publicly offered securities
                      </option>
                      <option value="14-Real property income and natural resources royalties">
                        14-Real property income and natural resources royalties
                      </option>
                      <option value="15-Pensions, annuities, alimony, and/or insurance premiums">
                        15-Pensions, annuities, alimony, and/or insurance premiums
                      </option>
                      <option value="16-Scholarship or fellowship grants">
                        16-Scholarship or fellowship grants
                      </option>
                      <option value="17-Compensation for independent personal services">
                        17-Compensation for independent personal services
                      </option>
                      <option value="21-Social Securities">21-Social Securities</option>
                      <option value="18-Compensation for dependent personal services">
                        18-Compensation for dependent personal services
                      </option>
                      <option value="19-Compensation for teaching">
                        19-Compensation for teaching
                      </option>
                      <option value="20-Compensation during studying and training">
                        20-Compensation during studying and training
                      </option>
                      <option value="21-Gross income-Capital Gain Dividend">
                        21-Gross income-Capital Gain Dividend
                      </option>
                      <option value="22-Interest paid on deposit with a foreign branch of a domestic corporation or partnership">
                        22-Interest paid on deposit with a foreign branch of a domestic corporation
                        or partnership
                      </option>
                      <option value="23-Other income">23-Other income</option>
                      <option value="23-Gross Income - Other">23-Gross Income - Other</option>
                      <option value="24-Qualified investment entity (QIE) distribution of capital gains">
                        24-Qualified investment entity (QIE) distribution of capital gains
                      </option>
                      <option value="24-Real estate investment trust (REIT) distributions of capital gains">
                        24-Real estate investment trust (REIT) distributions of capital gains
                      </option>
                      <option value="24-Qualified investment entity (QIE)">
                        24-Qualified investment entity (QIE)
                      </option>
                      <option value="25-Trust distributions subject to IRC section 1445">
                        25-Trust distributions subject to IRC section 1445
                      </option>
                      <option value="26-Unsevered growing crops and timber distributions by a trust subject to IRC section 1445">
                        26-Unsevered growing crops and timber distributions by a trust subject to
                        IRC section 1445
                      </option>
                      <option value="27-Publicly traded partnership distributions subject to IRC section 1446(a)">
                        27-Publicly traded partnership distributions subject to IRC section 1446(a)
                      </option>
                      <option value="38-Eligible deferred compensation items subject to IRC section 877A(d)(1)">
                        38-Eligible deferred compensation items subject to IRC section 877A(d)(1)
                      </option>
                      <option value="28-Gambling winnings">28-Gambling winnings</option>
                      <option value="29-Deposit interest">29-Deposit interest</option>
                      <option value="30-Original issue discount (OID)">
                        30-Original issue discount (OID)
                      </option>
                      <option value="39-Distributions from a nongrantor trust subject to IRC section 877A(f)(1)">
                        39-Distributions from a nongrantor trust subject to IRC section 877A(f)(1)
                      </option>
                      <option value="40-Other dividend equivalents under IRC section 871(m) (formely 871(l))">
                        40-Other dividend equivalents under IRC section 871(m) (formely 871(l))
                      </option>
                      <option value="31-Short-term OID">31-Short-term OID</option>
                      <option value="41-Guarantee of indebtedness">
                        41-Guarantee of indebtedness
                      </option>
                      <option value="32-Notional principal contract income">
                        32-Notional principal contract income
                      </option>
                      <option value="42-Earnings as an artist or athlete-no central withholding agreement">
                        42-Earnings as an artist or athlete-no central withholding agreement
                      </option>
                      <option value="33-Substitute payment - Interest">
                        33-Substitute payment - Interest
                      </option>
                      <option value="43-Earnings as an artist or athlete-central withholding agreement">
                        43-Earnings as an artist or athlete-central withholding agreement
                      </option>
                      <option value="34-Substitutive payments - dividends">
                        34-Substitutive payments - dividends
                      </option>
                      <option value="35-Substitute payment-other">
                        35-Substitute payment-other
                      </option>
                      <option value="36-Capital gains distributions">
                        36-Capital gains distributions
                      </option>
                      <option value="37-Return of capital">37-Return of capital</option>
                      <option value="44-Specified Federal procurement payments">
                        44-Specified Federal procurement payments
                      </option>
                      <option value="50-Income previously reported under escrow procedure">
                        50-Income previously reported under escrow procedure
                      </option>
                      <option value="51-Interest paid on certain actively traded or publicly offered securities">
                        51-Interest paid on certain actively traded or publicly offered securities
                      </option>
                      <option value="52-Dividends paid on certain actively traded or publicly offered securities">
                        52-Dividends paid on certain actively traded or publicly offered securities
                      </option>
                      <option value="53-Substitute payments - dividends from certain actively traded or publicly offered securities">
                        53-Substitute payments - dividends from certain actively traded or publicly
                        offered securities
                      </option>
                      <option value="54-Other income">54-Other income</option>
                      <option value="U.S. Effectively Connected Income (ECI)">
                        U.S. Effectively Connected Income (ECI)
                      </option>
                      <option value="54-Substitute payments - Interest from certain actively traded or publicly offered securities">
                        54-Substitute payments - Interest from certain actively traded or publicly
                        offered securities
                      </option>
                      <option value="55-Taxable death benefits on life insurance contracts">
                        55-Taxable death benefits on life insurance contracts
                      </option>
                      <option value="56-Dividend equivalents under IRC section 871(m) as a result of applying the combined transaction rules">
                        56-Dividend equivalents under IRC section 871(m) as a result of applying the
                        combined transaction rules
                      </option>
                      <option value="57-Amount realized under IRC section 1446(f)">
                        57-Amount realized under IRC section 1446(f)
                      </option>
                      <option value="58-Publicly Traded Partnership distributions - undetermined">
                        58-Publicly Traded Partnership distributions - undetermined
                      </option>
                      <option value="CRS 01 - Exchange of Information Income">
                        CRS 01 - Exchange of Information Income
                      </option>
                      <option value="UK 01 - United Kingdom or Crown Dependency Income">
                        UK 01 - United Kingdom or Crown Dependency Income
                      </option>
                    </select>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label
                      htmlFor={`incomeText-${idx}`}
                      style={{ fontWeight: 500, display: "block", marginBottom: 4 }}
                    >
                      Enter the text as it appears on the Form W-8ECI:
                    </label>
                    <textarea
                      id={`incomeText-${idx}`}
                      name="incomeText"
                      value={item.incomeText}
                      onChange={(e) => handleIncomeItemChange(idx, e)}
                      style={{
                        width: "100%",
                        minHeight: 80,
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        padding: 8,
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: 0 }}>
                    <label
                      htmlFor={`incomeAmount-${idx}`}
                      style={{ fontWeight: 500, display: "block", marginBottom: 4 }}
                    >
                      U.S. dollar amount (expected or estimated):
                    </label>
                    <input
                      type="text"
                      id={`incomeAmount-${idx}`}
                      name="incomeAmount"
                      value={item.incomeAmount}
                      onChange={(e) => handleIncomeItemChange(idx, e)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddIncomeItem}
                style={{
                  margin: "8px 0 12px 0",
                  background: "#0094e8",
                  color: "white",
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 2,
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                ADD ANOTHER
              </button>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <input
                  type="checkbox"
                  id="ECI-12_Securities_dealer"
                  name="ECI-12_Securities_dealer"
                  checked={
                    formData["ECI-12_Securities_dealer"] === "selected" ||
                    !!formData["ECI-12_Securities_dealer"]
                  }
                  onChange={handleChange}
                  style={{ marginRight: 8 }}
                />
                <label htmlFor="ECI-12_Securities_dealer" style={{ fontWeight: 500 }}>
                  Check here to certify that you are a dealer in securities :
                </label>
              </div>
            </section>

            {/* Part III: Certification */}
            <section style={sectionStyle}>
              <FormTitle value={"Part II Certification"} />
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    id="Signing_capacity"
                    checked={formData.Signing_capacity}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Signing_capacity",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="Signing_capacity" style={{ fontWeight: 500 }}>
                    I certify that I have the capacity to sign for the entity identified on line 1
                    of this form.
                  </label>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes", type: "radio" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no", type: "radio" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes", type: "radio" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no", type: "radio" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <FormInput
                  label={"Signed Name:"}
                  id={"Print_name"}
                  value={formData["Print_name"]}
                  name={"Print_name"}
                  onChange={handleChange}
                />
                {/* <FormInput label={"Signature:"} id={"Signature"} value={formData["Signature"]} name={"Signature"} onChange={handleChange} /> */}

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Signature_date"
                    value={formData["Signature_date"] || ""}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    id="Signature_unreadable"
                    checked={formData.Signature_unreadable || false}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Signature_unreadable",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="Signature_unreadable" style={{ fontWeight: 500 }}>
                    Check if signature is unreadable
                  </label>
                </div>
              </div>
            </section>
            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  padding: "6px 16px",
                  backgroundColor: InstaColors.primary,
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                {isSaving ? "Saving..." : "Save Form"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default W8ECIFORM;
